// The user hub's app-side identity — delegates and re-exports over
// `@heidi/workspace/data`, which now owns the `GET /user/` fetch as well as the
// reads it feeds.
//
// This file keeps its module identity on purpose: 547 production modules and
// 487 `jest.mock` literals name this specifier, so every symbol below resolves
// exactly as it did and no importer or mock changes.
//
// WHAT STAYS APP-SIDE IS THE EFFECTS, and they are split across two files
// rather than one, which is the thing to know before reading further. The
// per-render app reads live HERE — the Kinde region, the route verdict, the two
// dictation flag reads, the review-cohort atom, and `invalidateCurrentUser`,
// the one name that could not move. Everything the FETCH causes — the four
// toasts and their copy, the seen-modal write, the temp-user clear, the chat
// migration and the hard org-access redirect — lives in
// `src/composition/hosts/workspace/workspace-data-wiring.ts`, bound once on the seam rather than per
// caller, because a `queryFn` belongs to the query and not to an observer.
import {
  isDictationLanguageSettingsEnabled,
  isDictationOutputLanguageEnabled,
} from '@/features/flags';
import { GET_PRODUCTS } from '@/hooks/query/useQueryProducts';
import { getUser } from '@/lib/utils';
import { logger } from '@/utils/logger/logger';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { GET_USER } from '@heidi/workspace/contracts';
import {
  cancelCurrentUserQueries,
  getCurrentUser,
  getDefaultDictationInputLanguages as getDefaultDictationInputLanguagesCore,
  getDefaultDictationOutputLanguages as getDefaultDictationOutputLanguagesCore,
  getDictationSoundEnabled,
  getLogDictationsEnabled,
  isOrgAccessRequiredError,
  type QueryUserResponse,
  selectedReviewMemberAtom,
  useIsReviewingOtherUser as useIsReviewingOtherUserCore,
  useQueryUser,
  useQueryUserForContext as useQueryUserForContextCore,
  useUser,
} from '@heidi/workspace/data';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { useQueryKindeProperties } from './useQueryKindeProperties';
import { isUserQueryBlockedRoute } from './user-query-blocked-routes';

// Statement-level `export type`, never the inline `{ type X }` modifier: the
// inline form reads as a VALUE export to the react-refresh ratchet and to the
// package V3 fixtures. `@/context/current-user-context` re-exported this same
// declaration from the same door, so this drops a hop and nothing else.
export type { QueryUserResponse } from '@heidi/workspace/data';

// Re-exported so existing importers of these names from this file keep working
// unchanged. All four are door-backed now — the key in
// `@heidi/workspace/contracts`, the cache reader, the cancel command and the
// error classifier in `@heidi/workspace/data`, the last two on the seam's
// queryClient. `current-user-cache.ts` still exists, and still for its original
// reason: it forwards the key and the reader without this file's app-side
// import graph.
export { cancelCurrentUserQueries, GET_USER, getCurrentUser, isOrgAccessRequiredError };

// `AI_TEMPLATE_QUOTA_TOAST_TEST_ID` moved WITH ITS CONSUMER to
// `src/composition/hosts/workspace/workspace-data-wiring.ts`, where the quota toast now fires. The
// selector string is byte-identical — it is a live E2E hook read by
// `getByTestId('quota-toast')` — and no TypeScript module ever imported the
// constant, so leaving a re-export here would only have made this file an
// importer target for the wiring module and grown the family's ratchet by one
// pair for a name with no readers.

// Re-exported so existing importers of `isUserQueryBlockedRoute` from this file
// keep working unchanged. The definition lives in a dependency-free module so
// `useQueryKindeProperties` can also consume the predicate without a circular
// import (this module imports `useQueryKindeProperties`), and it stays app-side
// because a set of Next route patterns is the app's knowledge, not the domain's
// — the package's gate takes the RESOLVED boolean.
export { isUserQueryBlockedRoute };

/** User's preferred dictation input languages. Legacy `session_settings`
 * cover users who haven't re-saved via the new settings UI. */
export const getDefaultDictationInputLanguages = () =>
  getDefaultDictationInputLanguagesCore({
    languageSettingsEnabled: isDictationLanguageSettingsEnabled(),
  });

/** Output languages for the transcribe `user_context`. The override applies
 * only behind its own flag; otherwise output mirrors input. Exactly one of
 * `outputLanguages` (flag-on) / `outputLanguageCode` (legacy) is populated. */
export const getDefaultDictationOutputLanguages = () =>
  getDefaultDictationOutputLanguagesCore({
    languageSettingsEnabled: isDictationLanguageSettingsEnabled(),
    outputLanguageEnabled: isDictationOutputLanguageEnabled(),
  });

// Both read `getCurrentUser()` and nothing app-side, so they forward unwrapped.
export { getDictationSoundEnabled, getLogDictationsEnabled };

/**
 * Stays app-side, and the refusal is measured rather than assumed. This helper
 * is COMPOSITE — `GET_USER` is workspace's, `GET_PRODUCTS` is COMMERCE's, and
 * they share one try/catch, so reimplementing only the workspace leg behind the
 * door would silently drop a commerce invalidation for all of its callers
 * (the OT4 port rule).
 *
 * The composite cannot move whole either. Re-probed at this head by planting a
 * VALUE import of `GET_PRODUCTS` from `@heidi/commerce/contracts` into
 * `packages/workspace/src/data/query/useQueryUser.ts`; `pnpm migration:check`
 * returns two errors and the second is structural:
 *   [domain-edges] undeclared cross-domain value import workspace -> commerce
 *   [foundational] foundational domain "workspace" imports product domain(s) commerce
 * Probe reverted. `@heidi/commerce` already depends on `@heidi/workspace`, so
 * the edge is also a project cycle, not only a policy refusal.
 *
 * CONSEQUENCE FOR THE CENSUS, stated rather than netted off: this is the ONE
 * name of the five this seam scoped that does not forward, and the single leg
 * carrying it (`dictateOnboardingPersistence.ts`, which imports it alongside
 * the now-forwarded `getCurrentUser`) survives on this name alone.
 *
 * Its sibling `cancelCurrentUserQueries` DID move, and the asymmetry is the
 * whole rule: that one cancels `[GET_USER]` and nothing else.
 */
export const invalidateCurrentUser = async () => {
  try {
    await reactQueryClient.invalidateQueries({
      queryKey: [GET_USER],
    });

    void reactQueryClient.invalidateQueries({ queryKey: [GET_PRODUCTS] });
  } catch (error) {
    logger.error('Failed to invalidate user query:', error);
  }
};

/**
 * The app-side face of the `GET /user/` fetch owner. Signature-stable: callers
 * pass a selector or nothing, exactly as before.
 *
 * Two app-resolved values cross into the core — the Kinde region, which lives
 * in an app context, and the blocked-route verdict. Everything the fetch CAUSES
 * is bound once on the seam and is not visible here; see
 * `src/composition/hosts/workspace/workspace-data-wiring.ts`.
 */
export function useQueryUserForContext<T = QueryUserResponse>(
  select?: (data: QueryUserResponse) => T
) {
  const { data: kindeProperties } = useQueryKindeProperties();
  const router = useRouter();

  return useQueryUserForContextCore(
    {
      fetchCurrentUser: getUser,
      kindeRegion: kindeProperties?.region,
      isRouteBlocked: isUserQueryBlockedRoute(router.pathname),
    },
    select
  );
}

// The read delegates live in `@heidi/workspace/data` (Wave 2 · W — the user
// context-split). Re-exported as the door's own bindings, never wrapped: a
// Library importer's repoint from this file to the door is a pure specifier
// change only while reference equality holds, and `useQueryUser.test.ts`
// asserts it.
export { useQueryUser, useUser };

/**
 * Whether the caller is reviewing a team member's workspace rather than their
 * own. `selectedReviewMemberAtom` is an app atom, so it is read here and its
 * value crosses; the core owns the comparison against the cached user.
 */
export function useIsReviewingOtherUser() {
  const selectedUser = useAtomValue(selectedReviewMemberAtom);
  return useIsReviewingOtherUserCore(selectedUser);
}
